import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
  CircularProgress,
  Container,
  TextField,
  Button,
  InputAdornment,
} from '@mui/material';
import { Visibility, ThumbUp, Search, PlayCircleFilled } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from './auth/AuthContext';
import api from '../utils/api';

const AdminVideos = () => {
  const navigate = useNavigate();
  const { isAdminAuthenticated } = useAuth();
  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchVideos = async () => {
    if (!isAdminAuthenticated) {
      setError('Please login as admin first');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Try direct admin endpoint
      try {
        const response = await api.get('/api/videos/admin/my-videos');
        if (response.data?.length > 0) {
          setVideos(response.data);
          setFilteredVideos(response.data);
          return;
        }
      } catch (adminError) {
        console.warn('Admin endpoint failed, trying fallback:', adminError.message);
      }

      // Fallback to get all categories
      const categories = ['general', 'creative', 'innovation'];
      const allVideos = [];

      for (const category of categories) {
        try {
          const response = await api.get(`/api/videos/category/${category}`);
          allVideos.push(...response.data);
        } catch (categoryError) {
          console.warn(`Failed to fetch ${category} videos:`, categoryError);
        }
      }

      const adminVideos = allVideos.filter((v) =>
        v.uploadedBy && v.uploadedBy.toLowerCase() === 'gov_admin'
      );

      if (adminVideos.length === 0) {
        throw new Error('No admin videos found in any category');
      }

      setVideos(adminVideos);
      setFilteredVideos(adminVideos);
    } catch (err) {
      console.error('Final fetch error:', err);
      setError(err.message || 'Failed to fetch videos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, [isAdminAuthenticated]);

  useEffect(() => {
    if (searchQuery === '') {
      setFilteredVideos(videos);
    } else {
      const filtered = videos.filter((video) =>
        video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        video.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredVideos(filtered);
    }
  }, [searchQuery, videos]);

  const renderVideoItem = (item) => (
    <Grid item xs={12} sm={6} md={4} key={item._id}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', bgcolor: '#fff', overflow: 'hidden' }}>
          <CardActionArea>
            <Box sx={{ position: 'relative', height: 180 }}>
              <CardMedia
                component="img"
                image={item.thumbnailUrl || 'https://res.cloudinary.com/dtszhuotx/image/upload/v1743468602/download_1_mtgowl.jpg'}
                alt={item.title}
                sx={{ height: '100%', objectFit: 'cover', bgcolor: '#e1e1e1' }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  bgcolor: 'rgba(0,0,0,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PlayCircleFilled sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 50 }} />
              </Box>
            </Box>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 1 }}>
                {item.title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {item.description}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Visibility sx={{ color: '#666', fontSize: 16, mr: 0.5 }} />
                  <Typography variant="caption" sx={{ color: '#666', mr: 2 }}>{item.views}</Typography>
                  <ThumbUp sx={{ color: '#666', fontSize: 16, mr: 0.5 }} />
                  <Typography variant="caption" sx={{ color: '#666' }}>{item.likes}</Typography>
                </Box>
                <Box sx={{ bgcolor: '#f0e6ff', borderRadius: 2, px: 1.5, py: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#9C27B0', fontWeight: 'bold', textTransform: 'capitalize' }}>
                    {item.category}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
                Uploaded: {new Date(item.uploadedAt).toLocaleDateString()}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      </motion.div>
    </Grid>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        <CircularProgress size={60} sx={{ color: '#9C27B0' }} />
        <Typography variant="h6" sx={{ mt: 3, color: '#2c3e50' }}>Loading videos...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        <Typography variant="h6" sx={{ color: 'red', mb: 3, textAlign: 'center' }}>{error}</Typography>
        <Button
          variant="contained"
          onClick={fetchVideos}
          sx={{ bgcolor: '#9C27B0', '&:hover': { bgcolor: '#aa00cc' }, borderRadius: 2 }}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 3 }}>
          My Videos
        </Typography>
        <TextField
          fullWidth
          placeholder="Search videos..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#666' }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 4, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 4px rgba(0,2)' }}
        />
      </motion.div>
      {filteredVideos.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.2 }}>
          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Typography variant="h6" sx={{ color: '#666', mb: 3 }}>
              {searchQuery ? 'No matching videos found' : 'No videos uploaded yet'}
            </Typography>
            <Button
              variant="contained"
              onClick={fetchVideos}
              sx={{ bgcolor: '#9C27B0', '&:hover': { bgcolor: '#aa00cc' }, borderRadius: 2 }}
            >
              Refresh
            </Button>
          </Box>
        </motion.div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.2 }}>
          <Grid container spacing={3}>
            {filteredVideos.map((item) => renderVideoItem(item))}
          </Grid>
        </motion.div>
      )}
    </Container>
  );
};

export default AdminVideos;